import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { toast } from 'react-toastify';
import { notificationsApi } from '../../api/notifications';
import { usersApi } from '../../api/users';
import { providersApi } from '../../api/providers';
import {
  PageHeader,
  PageFilters,
  DataTable,
  TablePaginationBar,
  StatusBadge,
  ActionMenu,
} from '../../components/ui';
import type { Column } from '../../components/ui';
import { PAGE_SIZE } from '../../constants';
import { formatDateTime, paginate, matchesSearch } from '../../utils';
import type {
  AppNotification,
  Customer,
  NotificationTarget,
  ServiceProvider,
} from '../../types';

const TARGET_OPTIONS: NotificationTarget[] = ['Specific User', 'Specific Service Provider'];

const NotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [rows, setRows] = useState<AppNotification[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [providers, setProviders] = useState<ServiceProvider[]>([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [target, setTarget] = useState<NotificationTarget>('Specific User');
  const [selectedUser, setSelectedUser] = useState<Customer | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<ServiceProvider | null>(null);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const data = await notificationsApi.getNotifications({ page: 1, limit: 1000 });
      setRows(data.notifications);
    } catch {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
    usersApi
      .getUsers({ page: 1, limit: 1000 })
      .then((res) => setCustomers(res.users))
      .catch(() => undefined);
    providersApi
      .getProviders({ page: 1, limit: 1000 })
      .then((res) => setProviders(res.providers))
      .catch(() => undefined);
  }, []);

  const filtered = useMemo(() => {
    return rows.filter(
      (row) =>
        !search.trim() ||
        matchesSearch(row.id, search) ||
        matchesSearch(row.title, search) ||
        matchesSearch(row.target, search) ||
        matchesSearch(row.recipientName || '', search)
    );
  }, [rows, search]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const paged = paginate(filtered, page, PAGE_SIZE);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !message.trim()) {
      toast.error('Title and message are required');
      return;
    }
    if (target === 'Specific User' && !selectedUser) {
      toast.error('Select a user');
      return;
    }
    if (target === 'Specific Service Provider' && !selectedProvider) {
      toast.error('Select a service provider');
      return;
    }

    setSending(true);
    try {
      await notificationsApi.createNotification(title.trim(), message.trim());
      toast.success('Notification sent');
      setTitle('');
      setMessage('');
      setSelectedUser(null);
      setSelectedProvider(null);
      setTarget('Specific User');
      setTab(1);
      await loadHistory();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  const columns: Column<AppNotification>[] = [
    {
      id: 'id',
      label: 'ID',
      minWidth: 100,
      render: (row) => (
        <Typography variant="body2" fontWeight={600}>
          {row.id}
        </Typography>
      ),
    },
    {
      id: 'title',
      label: 'Title',
      minWidth: 160,
      render: (row) => row.title,
    },
    {
      id: 'target',
      label: 'Target',
      minWidth: 160,
      render: (row) => (
        <Box>
          <Typography variant="body2">{row.target}</Typography>
          {row.recipientName && (
            <Typography variant="caption" color="text.secondary">
              {row.recipientName}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      id: 'createdAt',
      label: 'Date',
      minWidth: 140,
      render: (row) => formatDateTime(row.createdAt),
    },
    {
      id: 'status',
      label: 'Delivery Status',
      minWidth: 120,
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      id: 'actions',
      label: 'Actions',
      align: 'right',
      render: (row) => (
        <ActionMenu
          actions={[
            {
              label: 'View',
              icon: <VisibilityOutlinedIcon fontSize="small" />,
              onClick: () => navigate(`/notifications/${row.id}`, { state: { notification: row } }),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Notifications"
        subtitle="Notify specific users or service providers"
      />
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Send Notification" />
        <Tab label="History" />
      </Tabs>

      {tab === 0 && (
        <Card variant="outlined" sx={{ borderRadius: 2, maxWidth: 720 }}>
          <CardContent>
            <Box component="form" onSubmit={handleSend}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    multiline
                    minRows={4}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Target</InputLabel>
                    <Select
                      label="Target"
                      value={target}
                      onChange={(e) => {
                        setTarget(e.target.value as NotificationTarget);
                        setSelectedUser(null);
                        setSelectedProvider(null);
                      }}
                    >
                      {TARGET_OPTIONS.map((opt) => (
                        <MenuItem key={opt} value={opt}>
                          {opt}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {target === 'Specific User' && (
                  <Grid item xs={12}>
                    <Autocomplete
                      options={customers}
                      getOptionLabel={(opt) => `${opt.name} (${opt.email})`}
                      value={selectedUser}
                      onChange={(_, value) => setSelectedUser(value)}
                      renderInput={(params) => (
                        <TextField {...params} label="Select User" required />
                      )}
                    />
                  </Grid>
                )}
                {target === 'Specific Service Provider' && (
                  <Grid item xs={12}>
                    <Autocomplete
                      options={providers}
                      getOptionLabel={(opt) => `${opt.name} (${opt.email})`}
                      value={selectedProvider}
                      onChange={(_, value) => setSelectedProvider(value)}
                      renderInput={(params) => (
                        <TextField {...params} label="Select Service Provider" required />
                      )}
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="secondary"
                    startIcon={<SendOutlinedIcon />}
                    disabled={sending}
                  >
                    {sending ? 'Sending…' : 'Send Notification'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>
      )}

      {tab === 1 && (
        <>
          <PageFilters
            search={search}
            searchPlaceholder="Search history…"
            onSearchChange={setSearch}
          />
          <DataTable
            columns={columns}
            rows={paged}
            loading={loading}
            getRowId={(row) => row.id}
            emptyTitle="No notifications yet"
          />
          <TablePaginationBar
            page={page}
            pageSize={PAGE_SIZE}
            total={filtered.length}
            onChange={setPage}
          />
        </>
      )}
    </Box>
  );
};

export default NotificationsPage;
